import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { RotateCw, Send, Trash2 } from 'lucide-react';
import { tenantsAPI } from '../../services/api';
import { TENANT_ROLE_OPTIONS, tenantRoleLabel } from '../../config/tenantRoles';
import { AppButton } from '../ui/AppButton';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { SectionCard } from '../ui/SectionCard';
import { SelectInput, TextInput } from './fields';

const dateLabel = (iso) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : '';

const nameOf = (member) => member.full_name || member.email || 'This member';

/**
 * What the invite email outcome (the `keycloak` field of an invite or resend
 * response) means for the admin. The invitation itself is always saved.
 */
const reportInviteOutcome = (outcome, email, organisation, { resend = false } = {}) => {
  if (outcome === 'ok') {
    toast.success(resend ? `Invitation resent to ${email}.` : `Invitation sent to ${email}.`);
  } else if (outcome === 'existing_account') {
    toast.success(
      `${email} already has a LiveBetter account and was added to ${organisation}. `
      + 'No e-mail is needed: they see it the next time they sign in.'
    );
  } else if (outcome === 'no_group') {
    toast.warning(
      `The invitation is saved, but no email was sent: ${organisation} has no sign-on group yet. `
      + 'A platform administrator needs to set it up.'
    );
  } else if (outcome === 'error') {
    toast.warning(
      'The invitation is saved, but the email could not be sent. Email sending may not be set up on this Hub: '
      + 'ask a platform administrator to check it, then use Resend.'
    );
  } else {
    toast.warning('The invitation is saved, but this Hub is not set up to send sign-on emails.');
  }
};

const InviteForm = ({ tenant, organisation, onInvited }) => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('tenant_user');
  const [sending, setSending] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    const address = email.trim();
    if (!address) return;
    setSending(true);
    try {
      const result = await tenantsAPI.inviteMember(tenant.id, { email: address, role });
      reportInviteOutcome(result?.keycloak, address, organisation);
      setEmail('');
      setRole('tenant_user');
      onInvited();
    } catch (error) {
      toast.error(error?.message || 'The invitation could not be sent.');
    } finally {
      setSending(false);
    }
  };

  return (
    <SectionCard
      title="Invite a member"
      subtitle={`New people get an e-mail to set their password, then land in the Hub. People who already have a LiveBetter account are added to ${organisation} straight away.`}
    >
      <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_160px_auto] gap-2 items-center">
        <TextInput
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder="name@organisation.eu"
          aria-label="Email address"
          autoComplete="off"
          required
        />
        <SelectInput value={role} onChange={(event) => setRole(event.target.value)} aria-label="Role">
          {TENANT_ROLE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </SelectInput>
        <AppButton
          type="submit"
          variant="primary"
          icon={<Send size={14} aria-hidden="true" />}
          loading={sending}
          disabled={!email.trim()}
        >
          Send invite
        </AppButton>
      </form>
    </SectionCard>
  );
};

const MemberRow = ({ member, isSelf, busy, onRoleChange, onRemove }) => (
  <li className="grid grid-cols-[minmax(0,1fr)_34px] sm:grid-cols-[minmax(0,1fr)_160px_34px] gap-x-3 gap-y-2 items-center py-3 border-t border-black/[0.07] first:border-t-0 first:pt-0 last:pb-0">
    <span className="min-w-0">
      <span className="block truncate text-[14px] font-semibold text-lb-ink">
        {nameOf(member)}{isSelf ? ' (you)' : ''}
      </span>
      {member.full_name && member.email && (
        <span className="block truncate text-[12px] text-lb-muted">{member.email}</span>
      )}
    </span>
    {isSelf ? (
      <span className="col-span-2 sm:col-span-1 sm:col-start-2 text-[13px] font-semibold text-lb-muted px-1">
        {tenantRoleLabel(member.role)}
      </span>
    ) : (
      <>
        <SelectInput
          value={member.role}
          onChange={(event) => onRoleChange(member, event.target.value)}
          disabled={busy}
          aria-label={`Role for ${nameOf(member)}`}
          className="order-3 col-span-2 sm:order-none sm:col-span-1"
        >
          {TENANT_ROLE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </SelectInput>
        <button
          type="button"
          onClick={() => onRemove(member)}
          disabled={busy}
          aria-label={`Remove ${nameOf(member)}`}
          className="w-[34px] h-[34px] flex items-center justify-center rounded-[10px] border border-[rgba(184,80,82,0.32)] bg-[#FAFAF7] text-lb-critical cursor-pointer disabled:opacity-45 disabled:cursor-not-allowed outline-none focus-ring"
        >
          <Trash2 size={14} aria-hidden="true" />
        </button>
      </>
    )}
  </li>
);

const InvitationRow = ({ invitation, busy, onResend, onRevoke }) => (
  <li className="flex flex-wrap items-center gap-x-3 gap-y-2 py-3 border-t border-black/[0.07] first:border-t-0 first:pt-0 last:pb-0">
    <span className="min-w-0 flex-1 basis-[220px]">
      <span className="block truncate text-[14px] font-semibold text-lb-ink">{invitation.email}</span>
      <span className="block text-[12px] text-lb-muted">
        {tenantRoleLabel(invitation.role)} · sent {dateLabel(invitation.created_at)} · expires {dateLabel(invitation.expires_at)}
      </span>
    </span>
    <span className="flex gap-2">
      <AppButton
        variant="secondary"
        size="sm"
        icon={<RotateCw size={13} aria-hidden="true" />}
        disabled={busy}
        onClick={() => onResend(invitation)}
        aria-label={`Resend invitation to ${invitation.email}`}
      >
        Resend
      </AppButton>
      <AppButton
        variant="ghost"
        size="sm"
        disabled={busy}
        onClick={() => onRevoke(invitation)}
        aria-label={`Revoke invitation to ${invitation.email}`}
      >
        Revoke
      </AppButton>
    </span>
  </li>
);

/**
 * Members of the active organisation, for its admins: invite people, change
 * their organisation role, remove them, and follow up pending invitations.
 */
export const MembersPanel = ({ tenant, currentUserId }) => {
  const organisation = tenant.name || tenant.slug;
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [pendingRemoval, setPendingRemoval] = useState(null);
  const [removing, setRemoving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [memberData, inviteData] = await Promise.all([
        tenantsAPI.listMembers(tenant.id),
        tenantsAPI.listInvitations(tenant.id, { status: 'pending' }),
      ]);
      setMembers(memberData?.members || []);
      setInvitations(inviteData?.invitations || []);
      setLoadError(null);
    } catch (error) {
      setLoadError(error?.message || 'Members could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [tenant.id]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  const changeRole = async (member, role) => {
    setBusyId(member.user_id);
    try {
      await tenantsAPI.updateMember(tenant.id, member.user_id, { role });
      setMembers((previous) => previous.map((m) => (m.user_id === member.user_id ? { ...m, role } : m)));
      toast.success(`${nameOf(member)} is now ${tenantRoleLabel(role)}.`);
    } catch (error) {
      toast.error(error?.message || 'The role could not be changed.');
    } finally {
      setBusyId(null);
    }
  };

  const confirmRemoval = async () => {
    const member = pendingRemoval;
    setRemoving(true);
    try {
      await tenantsAPI.removeMember(tenant.id, member.user_id);
      setMembers((previous) => previous.filter((m) => m.user_id !== member.user_id));
      toast.success(`${nameOf(member)} was removed from ${organisation}.`);
      setPendingRemoval(null);
    } catch (error) {
      toast.error(error?.message || 'The member could not be removed.');
    } finally {
      setRemoving(false);
    }
  };

  const resend = async (invitation) => {
    setBusyId(invitation.id);
    try {
      const result = await tenantsAPI.resendInvitation(tenant.id, invitation.id);
      reportInviteOutcome(result?.keycloak, invitation.email, organisation, { resend: true });
      await load();
    } catch (error) {
      toast.error(error?.message || 'The invitation could not be resent.');
    } finally {
      setBusyId(null);
    }
  };

  const revoke = async (invitation) => {
    setBusyId(invitation.id);
    try {
      await tenantsAPI.revokeInvitation(tenant.id, invitation.id);
      setInvitations((previous) => previous.filter((i) => i.id !== invitation.id));
      toast.success(`Invitation to ${invitation.email} revoked.`);
    } catch (error) {
      toast.error(error?.message || 'The invitation could not be revoked.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <InviteForm tenant={tenant} organisation={organisation} onInvited={load} />

      <SectionCard
        title="Members"
        subtitle="What someone can open comes from their persona. The organisation role only adds or removes the following."
      >
        <dl className="m-0 mb-5 grid grid-cols-[max-content_minmax(0,1fr)] gap-x-4 gap-y-1.5 text-[13px]" aria-label="Organisation roles">
          {TENANT_ROLE_OPTIONS.map((option) => (
            <React.Fragment key={option.value}>
              <dt className="font-semibold text-lb-ink">{option.label}</dt>
              <dd className="m-0 text-lb-muted">{option.description}</dd>
            </React.Fragment>
          ))}
        </dl>
        {loading ? (
          <p className="m-0 text-[13px] text-lb-muted">Loading members…</p>
        ) : loadError ? (
          <div className="flex flex-wrap items-center gap-3">
            <p className="m-0 text-[13px] text-lb-critical-text">{loadError}</p>
            <AppButton variant="secondary" size="sm" onClick={load}>Try again</AppButton>
          </div>
        ) : members.length === 0 ? (
          <p className="m-0 text-[13px] text-lb-muted">No members yet.</p>
        ) : (
          <ul className="m-0 p-0 list-none flex flex-col" aria-label={`Members of ${organisation}`}>
            {members.map((member) => (
              <MemberRow
                key={member.user_id}
                member={member}
                isSelf={member.user_id === currentUserId}
                busy={busyId === member.user_id}
                onRoleChange={changeRole}
                onRemove={setPendingRemoval}
              />
            ))}
          </ul>
        )}
      </SectionCard>

      {!loading && !loadError && invitations.length > 0 && (
        <SectionCard
          title="Pending invitations"
          subtitle="Accepted, revoked and expired invitations are not listed. Resend renews an invitation for 7 days."
        >
          <ul className="m-0 p-0 list-none flex flex-col" aria-label="Pending invitations">
            {invitations.map((invitation) => (
              <InvitationRow
                key={invitation.id}
                invitation={invitation}
                busy={busyId === invitation.id}
                onResend={resend}
                onRevoke={revoke}
              />
            ))}
          </ul>
        </SectionCard>
      )}

      <p className="m-0 text-[12px] leading-[1.45] text-lb-muted">
        Access to Business Cases, personas and AI services is set in Keycloak, not here.
      </p>

      <ConfirmDialog
        open={Boolean(pendingRemoval)}
        title={pendingRemoval ? `Remove ${nameOf(pendingRemoval)}?` : ''}
        message={`They will lose access to ${organisation} in the Hub. Their sign-on account is not deleted.`}
        confirmLabel="Remove member"
        onConfirm={confirmRemoval}
        onCancel={() => setPendingRemoval(null)}
        busy={removing}
      />
    </div>
  );
};
